import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./CxInfrastructure.css";
import Testimonial from "../../../Testimonial/Testimonial";

const CxInfrastructure = () => {
  const techItemsRow1 = [
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "HTML", logo: "/logos/html.png" },
    { name: "CSS3", logo: "/logos/css.png" },
    { name: "Bootstrap", logo: "/logos/bootstrap.png" },
    { name: "Javascript", logo: "/logos/javascript.png" },
    { name: "AmazonDB", logo: "/logos/amazon_dynamodb.png" },
    { name: "Appian", logo: "/logos/appian.png" },
    { name: "Laravel", logo: "/logos/laravel.png" },
    { name: "MariaDB", logo: "/logos/mariadb.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "Android", logo: "/logos/android-5.png" },
    { name: "ERC-20", logo: "/logos/erc-20.png" },
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Flutter", logo: "/logos/flutter.png" },
    { name: "Ionic", logo: "/logos/ionic.png" },
    { name: "Augmented Reality", logo: "/logos/Augmented Reality.png" },
    { name: "Crda", logo: "/logos/crda.png" },
    { name: "Data Science", logo: "/logos/data-science.png" },
    { name: "Go Chain", logo: "/logos/gochain.png" },
  ];

  const techItemsRow2 = [
    { name: "Sass", logo: "/logos/saas.png" },
    { name: "JQuery", logo: "/logos/jquery.png" },
    { name: "Less", logo: "/logos/less.png" },
    { name: "Codeigniter", logo: "/logos/codeignitor.png" },
    { name: "Django", logo: "/logos/django.png" },
    { name: "Node JS", logo: "/logos/node js.png" },
    { name: "PHP", logo: "/logos/php.png" },
    { name: "Salesforce", logo: "/logos/salesforce.png" },
    { name: "SQL Server", logo: "/logos/sql-server.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "IOS", logo: "/logos/ios.png" },
    { name: "Oracle 6", logo: "/logos/oracle.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "React Native", logo: "/logos/react_native.png" },
    { name: "Solidity", logo: "/logos/Solidity.png" },
    { name: "Hyperledger", logo: "/logos/Hyperledger.png" },
    { name: "Internet Of Things", logo: "/logos/iot.png" },
    { name: "One Ledger", logo: "/logos/OneLedger.png" },
    { name: "Virtual Reality", logo: "/logos/virtual-reality.png" },
  ];

  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    // Set initial container width
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1; // Adjust speed as needed (pixels per frame)

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);

  const serviceCardsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("cx-infra-visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    serviceCardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => {
      serviceCardsRef.current.forEach((card) => {
        if (card) observer.unobserve(card);
      });
    };
  }, []);

  // Add ref to each service card
  const addToRefs = (el) => {
    if (el && !serviceCardsRef.current.includes(el)) {
      serviceCardsRef.current.push(el);
    }
  };

  return (
    <div>
      <Helmet>
        <title>CX Infrastructure | Our Tech Stack and Tools</title>
        <meta
          name="description"
          content="Explore our comprehensive tech stack including Angular, React, Node.js, blockchain technologies, and more. See the tools we use to build innovative solutions."
        />
        <meta
          name="keywords"
          content="tech stack, development tools, angular, react, node.js, blockchain, web development, mobile development, database technologies, AI, IoT"
        />
      </Helmet>

      <div className="cx-infra-container">
        <div className="cx-infra-hero">
          <div className="cx-infra-hero-content">
            <h1 className="cx-infra-title">CX Infrastructure Solutions</h1>
            <p className="cx-infra-subtitle">
              Building the Foundation for Exceptional Customer Experiences
            </p>
          </div>
          <div className="cx-infra-hero-animation">
            <div className="cx-infra-node cx-infra-node-1"></div>
            <div className="cx-infra-node cx-infra-node-2"></div>
            <div className="cx-infra-node cx-infra-node-3"></div>
            <div className="cx-infra-connection cx-infra-conn-1"></div>
            <div className="cx-infra-connection cx-infra-conn-2"></div>
            <div className="cx-infra-connection cx-infra-conn-3"></div>
          </div>
        </div>

        <section className="cx-infra-section cx-infra-section-dark">
          <div className="cx-infra-section-content">
            <h2 className="cx-infra-section-title">
              What is CX Infrastructure?
            </h2>
            <p className="cx-infra-section-text">
              CX Infrastructure refers to the underlying technology framework
              that enables seamless, personalized, and efficient customer
              experiences across all touchpoints. Our solutions integrate
              cutting-edge technologies to create a robust foundation for your
              customer engagement strategies.
            </p>
          </div>
        </section>

        <section className="cx-infra-section">
          <h2 className="cx-infra-section-title cx-infra-centered">
            Our CX Infrastructure Services
          </h2>
          <div className="cx-infra-services-grid">
            <div className="cx-infra-service-card">
              <div className="cx-infra-service-icon cx-infra-icon-1">
                <i className="fas fa-network-wired"></i>
              </div>
              <h3 className="cx-infra-service-title">Cloud Integration</h3>
              <p className="cx-infra-service-desc">
                Seamlessly connect your systems with our cloud integration
                solutions for unified customer data.
              </p>
            </div>

            <div className="cx-infra-service-card">
              <div className="cx-infra-service-icon cx-infra-icon-2">
                <i className="fas fa-database"></i>
              </div>
              <h3 className="cx-infra-service-title">Data Management</h3>
              <p className="cx-infra-service-desc">
                Centralize and manage customer data with our secure and scalable
                infrastructure solutions.
              </p>
            </div>

            <div className="cx-infra-service-card">
              <div className="cx-infra-service-icon cx-infra-icon-3">
                <i className="fas fa-shield-alt"></i>
              </div>
              <h3 className="cx-infra-service-title">Security Solutions</h3>
              <p className="cx-infra-service-desc">
                Protect customer information with our enterprise-grade security
                infrastructure and protocols.
              </p>
            </div>

            <div className="cx-infra-service-card">
              <div className="cx-infra-service-icon cx-infra-icon-4">
                <i className="fas fa-robot"></i>
              </div>
              <h3 className="cx-infra-service-title">AI & Automation</h3>
              <p className="cx-infra-service-desc">
                Implement intelligent automation to enhance customer
                interactions and operational efficiency.
              </p>
            </div>

            <div className="cx-infra-service-card">
              <div className="cx-infra-service-icon cx-infra-icon-5">
                <i className="fas fa-headset"></i>
              </div>
              <h3 className="cx-infra-service-title">Omnichannel Support</h3>
              <p className="cx-infra-service-desc">
                Deliver consistent customer experiences across all channels with
                our integrated support solutions.
              </p>
            </div>

            <div className="cx-infra-service-card">
              <div className="cx-infra-service-icon cx-infra-icon-6">
                <i className="fas fa-chart-line"></i>
              </div>
              <h3 className="cx-infra-service-title">Analytics & Insights</h3>
              <p className="cx-infra-service-desc">
                Transform customer data into actionable insights with our
                advanced analytics platforms.
              </p>
            </div>

            <div className="cx-infra-service-card">
              <div className="cx-infra-service-icon cx-infra-icon-7">
                <i className="fas fa-mobile-alt"></i>
              </div>
              <h3 className="cx-infra-service-title">Mobile Experience</h3>
              <p className="cx-infra-service-desc">
                Create seamless mobile customer experiences with our responsive
                infrastructure solutions.
              </p>
            </div>

            <div className="cx-infra-service-card">
              <div className="cx-infra-service-icon cx-infra-icon-8">
                <i className="fas fa-sync-alt"></i>
              </div>
              <h3 className="cx-infra-service-title">
                Continuous Optimization
              </h3>
              <p className="cx-infra-service-desc">
                Continuously improve your CX infrastructure with our monitoring
                and optimization tools.
              </p>
            </div>

            <div className="cx-infra-service-card">
              <div className="cx-infra-service-icon cx-infra-icon-9">
                <i className="fas fa-users-cog"></i>
              </div>
              <h3 className="cx-infra-service-title">Workforce Engagement</h3>
              <p className="cx-infra-service-desc">
                Empower your customer service team with tools for better
                engagement and productivity.
              </p>
            </div>

            <div className="cx-infra-service-card">
              <div className="cx-infra-service-icon cx-infra-icon-10">
                <i className="fas fa-lock"></i>
              </div>
              <h3 className="cx-infra-service-title">Compliance Management</h3>
              <p className="cx-infra-service-desc">
                Ensure regulatory compliance with our built-in governance and
                compliance frameworks.
              </p>
            </div>

            {/* Two Additional Services */}
            <div className="cx-infra-service-card">
              <div className="cx-infra-service-icon cx-infra-icon-11">
                <i className="fas fa-bolt"></i>
              </div>
              <h3 className="cx-infra-service-title">
                Real-time Personalization
              </h3>
              <p className="cx-infra-service-desc">
                Deliver tailored experiences with our real-time personalization
                engines that adapt to customer behavior.
              </p>
            </div>

            <div className="cx-infra-service-card">
              <div className="cx-infra-service-icon cx-infra-icon-12">
                <i className="fas fa-infinity"></i>
              </div>
              <h3 className="cx-infra-service-title">CX Journey Mapping</h3>
              <p className="cx-infra-service-desc">
                Visualize and optimize every touchpoint in the customer journey
                with our advanced mapping tools.
              </p>
            </div>
          </div>
        </section>

        <section className="cx-infra-section cx-infra-section-dark">
  <h2 className="cx-infra-section-title cx-infra-centered">
    Benefits of Our CX Infrastructure
  </h2>
  <div className="cx-infra-benefits">
    <div className="cx-infra-benefit-item">
      <div className="cx-infra-benefit-number">01</div>
      <h3 className="cx-infra-benefit-title">Enhanced Scalability</h3>
      <p className="cx-infra-benefit-desc">
        Our infrastructure grows with your business, ensuring consistent
        performance during peak demand.
      </p>
    </div>

    <div className="cx-infra-benefit-item">
      <div className="cx-infra-benefit-number">02</div>
      <h3 className="cx-infra-benefit-title">Seamless Integration</h3>
      <p className="cx-infra-benefit-desc">
        Connect all your customer touchpoints for a unified view of
        customer interactions.
      </p>
    </div>

    <div className="cx-infra-benefit-item">
      <div className="cx-infra-benefit-number">03</div>
      <h3 className="cx-infra-benefit-title">Real-time Analytics</h3>
      <p className="cx-infra-benefit-desc">
        Gain actionable insights from customer data with our advanced
        analytics capabilities.
      </p>
    </div>
    
    {/* Additional Benefits */}
    <div className="cx-infra-benefit-item">
      <div className="cx-infra-benefit-number">04</div>
      <h3 className="cx-infra-benefit-title">Improved Customer Retention</h3>
      <p className="cx-infra-benefit-desc">
        Enhance customer loyalty with personalized experiences that keep customers coming back.
      </p>
    </div>

    <div className="cx-infra-benefit-item">
      <div className="cx-infra-benefit-number">05</div>
      <h3 className="cx-infra-benefit-title">Cost Efficiency</h3>
      <p className="cx-infra-benefit-desc">
        Reduce operational costs with automated processes and optimized resource allocation.
      </p>
    </div>

    <div className="cx-infra-benefit-item">
      <div className="cx-infra-benefit-number">06</div>
      <h3 className="cx-infra-benefit-title">Enhanced Security</h3>
      <p className="cx-infra-benefit-desc">
        Protect sensitive customer data with enterprise-grade security protocols and compliance.
      </p>
    </div>

    <div className="cx-infra-benefit-item">
      <div className="cx-infra-benefit-number">07</div>
      <h3 className="cx-infra-benefit-title">Faster Response Times</h3>
      <p className="cx-infra-benefit-desc">
        Deliver quicker resolutions with streamlined workflows and intelligent routing.
      </p>
    </div>

    <div className="cx-infra-benefit-item">
      <div className="cx-infra-benefit-number">08</div>
      <h3 className="cx-infra-benefit-title">360° Customer View</h3>
      <p className="cx-infra-benefit-desc">
        Access complete customer profiles with integrated data from all touchpoints.
      </p>
    </div>

    <div className="cx-infra-benefit-item">
      <div className="cx-infra-benefit-number">09</div>
      <h3 className="cx-infra-benefit-title">Future-Proof Technology</h3>
      <p className="cx-infra-benefit-desc">
        Stay ahead with scalable architecture that adapts to emerging technologies and trends.
      </p>
    </div>
  </div>
</section>

        <section className="cx-infra-section">
          <div className="cx-infra-cta">
            <h2 className="cx-infra-cta-title">
              Ready to Transform Your CX Infrastructure?
            </h2>
            <p className="cx-infra-cta-text">
              Contact our experts today to discuss how we can build a robust
              foundation for your customer experience initiatives.
            </p>
            <a href="/contact-us">
              {" "}
              <button className="cx-infra-cta-button">Get in Touch</button>
            </a>
          </div>
        </section>
      </div>

      <Testimonial />

      <div className="tech-slider-container" ref={containerRef}>
        <h2 className="tech-slider-title">Tools & Tech Stack</h2>
        <div className="tech-slider-divider"></div>

        {/* First row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row1Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow1.map((tech, index) => (
              <div key={`row1-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Second row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row2Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow2.map((tech, index) => (
              <div key={`row2-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CxInfrastructure;
